import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import Button from '../../components/common/Button';
import PremiumFeature from '../../components/shared/PremiumFeature';
import { useTheme } from '../../hooks/useTheme';

// Dummy data for clothing items
const DUMMY_CLOSET = [
  {
    id: '1',
    name: 'Blue Shirt',
    image: 'https://via.placeholder.com/200',
    tags: ['blue', 'casual', 'summer'],
    category: 'top',
  },
  {
    id: '2',
    name: 'White T-Shirt',
    image: 'https://via.placeholder.com/200',
    tags: ['white', 'casual', 'summer'],
    category: 'top',
  },
  {
    id: '3',
    name: 'Black Pants',
    image: 'https://via.placeholder.com/200',
    tags: ['black', 'formal', 'versatile'],
    category: 'bottom',
  },
  {
    id: '4',
    name: 'Blue Jeans',
    image: 'https://via.placeholder.com/200',
    tags: ['blue', 'casual', 'versatile'],
    category: 'bottom',
  },
  {
    id: '5',
    name: 'Brown Leather Jacket',
    image: 'https://via.placeholder.com/200',
    tags: ['brown', 'casual', 'winter'],
    category: 'outerwear',
  },
  {
    id: '6',
    name: 'Navy Blazer',
    image: 'https://via.placeholder.com/200',
    tags: ['navy', 'formal', 'spring'],
    category: 'outerwear',
  },
];

// Dummy palette data
const USER_PALETTE = {
  id: 1,
  name: 'My Spring Palette',
  skinTone: 'Warm',
  colors: ['#8ECAE6', '#219EBC', '#023047', '#FFB703', '#FB8500'],
};

// Mock weather data
const WEATHER_DATA = {
  temperature: 72,
  condition: 'sunny',
  description: 'Sunny with clear skies',
  humidity: 65,
  wind: 5,
  icon: '☀️',
};

const WearThisTodayScreen = () => {
  useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { colors } = useTheme();

  const [loading, setLoading] = useState(true);
  const [outfit, setOutfit] = useState<{
    top: (typeof DUMMY_CLOSET)[0] | null;
    bottom: (typeof DUMMY_CLOSET)[0] | null;
    outerwear: (typeof DUMMY_CLOSET)[0] | null;
  }>({
    top: null,
    bottom: null,
    outerwear: null,
  });
  const [weather] = useState(WEATHER_DATA);

  // Simulate loading data
  useEffect(() => {
    const timer = setTimeout(() => {
      // Generate outfit recommendation based on weather and palette
      const tops = DUMMY_CLOSET.filter((item) => item.category === 'top');
      const bottoms = DUMMY_CLOSET.filter((item) => item.category === 'bottom');
      const outerwears = DUMMY_CLOSET.filter(
        (item) => item.category === 'outerwear'
      );

      // Simple recommendation logic (in reality, this would be more sophisticated)
      // For demo purposes, we randomly select items
      const randomTop = tops[Math.floor(Math.random() * tops.length)];
      const randomBottom = bottoms[Math.floor(Math.random() * bottoms.length)];

      // Only recommend outerwear if it's cool
      const randomOuterwear =
        weather.temperature < 75
          ? outerwears[Math.floor(Math.random() * outerwears.length)]
          : null;

      setOutfit({
        top: randomTop,
        bottom: randomBottom,
        outerwear: randomOuterwear,
      });

      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [weather.temperature]);

  const generateNewOutfit = () => {
    setLoading(true);

    // Simulate API call
    setTimeout(() => {
      // Similar logic as above but guarantees a different outfit
      const tops = DUMMY_CLOSET.filter(
        (item) => item.category === 'top' && item.id !== outfit.top?.id
      );
      const bottoms = DUMMY_CLOSET.filter(
        (item) => item.category === 'bottom' && item.id !== outfit.bottom?.id
      );
      const outerwears = DUMMY_CLOSET.filter(
        (item) =>
          item.category === 'outerwear' && item.id !== outfit.outerwear?.id
      );

      const randomTop =
        tops.length > 0
          ? tops[Math.floor(Math.random() * tops.length)]
          : DUMMY_CLOSET.filter((item) => item.category === 'top')[0];

      const randomBottom =
        bottoms.length > 0
          ? bottoms[Math.floor(Math.random() * bottoms.length)]
          : DUMMY_CLOSET.filter((item) => item.category === 'bottom')[0];

      // Only recommend outerwear if it's cool
      const randomOuterwear =
        weather.temperature < 75
          ? outerwears.length > 0
            ? outerwears[Math.floor(Math.random() * outerwears.length)]
            : DUMMY_CLOSET.filter((item) => item.category === 'outerwear')[0]
          : null;

      setOutfit({
        top: randomTop,
        bottom: randomBottom,
        outerwear: randomOuterwear,
      });

      setLoading(false);
    }, 1000);
  };

  const renderClothingItem = (
    item: (typeof DUMMY_CLOSET)[0] | null,
    label: string
  ) => {
    if (!item) return null;

    return (
      <View style={styles.clothingItem}>
        <Text style={[styles.clothingLabel, { color: colors.secondary }]}>
          {label}:
        </Text>
        <View
          style={[
            styles.clothingCard,
            {
              backgroundColor: colors.card,
              borderColor: colors.border,
              ...(Platform.OS === 'web'
                ? {
                    boxShadow: `0px 2px 8px 0px ${colors.cardText}1A`,
                  }
                : { shadowColor: colors.cardText }),
            },
          ]}
        >
          <Image
            source={{ uri: item.image }}
            style={[styles.clothingImage, { backgroundColor: colors.border }]}
          />
          <Text style={[styles.clothingName, { color: colors.text }]}>
            {item.name}
          </Text>
          <View style={styles.tagsContainer}>
            {item.tags.map((tag, index) => (
              <View
                key={index}
                style={[styles.tag, { backgroundColor: colors.border }]}
              >
                <Text style={[styles.tagText, { color: colors.supportText }]}>
                  {tag}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  };

  const outfitContent = (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <ScrollView style={styles.scrollView}>
        <View style={styles.content}>
          <View
            style={[
              styles.weatherCard,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
          >
            <Text style={[styles.weatherTitle, { color: colors.secondary }]}>
              Today&apos;s Weather
            </Text>
            <View style={styles.weatherInfo}>
              <Text style={styles.weatherIcon}>{weather.icon}</Text>
              <View style={styles.weatherDetails}>
                <Text style={[styles.weatherTemp, { color: colors.text }]}>
                  {weather.temperature}°F
                </Text>
                <Text style={[styles.weatherDesc, { color: colors.secondary }]}>
                  {weather.description}
                </Text>
                <Text
                  style={[styles.weatherExtra, { color: colors.secondary }]}
                >
                  Humidity: {weather.humidity}% • Wind: {weather.wind} mph
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.paletteSection}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              Your Colour Palette
            </Text>
            <Text style={[styles.skinTone, { color: colors.secondary }]}>
              Skin Tone: {USER_PALETTE.skinTone}
            </Text>
            <View
              style={[
                styles.paletteContainer,
                { backgroundColor: colors.card, borderColor: colors.border },
              ]}
            >
              {USER_PALETTE.colors.map((color, index) => (
                <View
                  key={index}
                  style={[
                    styles.paletteColor,
                    { backgroundColor: color, borderColor: colors.border },
                  ]}
                />
              ))}
            </View>
          </View>

          <View style={styles.outfitSection}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              Today&apos;s Recommended Outfit
            </Text>
            {loading ? (
              <View
                style={[
                  styles.loadingContainer,
                  { backgroundColor: colors.card },
                ]}
              >
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={[styles.loadingText, { color: colors.secondary }]}>
                  Generating perfect outfit...
                </Text>
              </View>
            ) : (
              <View
                style={[
                  styles.outfitContainer,
                  { backgroundColor: colors.card },
                ]}
              >
                {renderClothingItem(outfit.top, 'Top')}
                {renderClothingItem(outfit.bottom, 'Bottom')}
                {renderClothingItem(outfit.outerwear, 'Outerwear')}

                <Button
                  title="Generate Another Outfit"
                  onPress={generateNewOutfit}
                  style={styles.generateButton}
                  trackClick={true}
                  trackingLocation="wear-this-today-screen"
                  trackingData={{
                    button_name: 'Generate Another Outfit',
                    button_type: 'generate_another_outfit',
                    screen: 'wear_this_today',
                  }}
                />
              </View>
            )}
          </View>

          <View style={styles.suggestionSection}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              Why We Chose This
            </Text>
            <Text
              style={[
                styles.suggestionText,
                { color: colors.secondary, backgroundColor: colors.card },
              ]}
            >
              Based on today&apos;s weather ({weather.temperature}°F,{' '}
              {weather.condition}) and your warm skin tone, we&apos;ve selected
              colors that complement your natural palette. The outfit balances
              comfort for the temperature while ensuring the colors work well
              together.
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );

  return <PremiumFeature>{outfitContent}</PremiumFeature>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 60,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 8,
  },
  backButtonText: {
    fontSize: 16,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  placeholder: {
    width: 50,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  weatherCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    borderWidth: 1,
  },
  weatherTitle: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 12,
  },
  weatherInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  weatherIcon: {
    fontSize: 40,
    marginRight: 16,
  },
  weatherDetails: {
    flex: 1,
  },
  weatherTemp: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  weatherDesc: {
    fontSize: 16,
    marginBottom: 4,
  },
  weatherExtra: {
    fontSize: 12,
  },
  paletteSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  skinTone: {
    fontSize: 14,
    marginBottom: 8,
  },
  paletteContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  paletteColor: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: 1,
  },
  outfitSection: {
    marginBottom: 24,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    borderRadius: 8,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
  },
  outfitContainer: {
    borderRadius: 8,
    padding: 16,
  },
  clothingItem: {
    marginBottom: 16,
  },
  clothingLabel: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
  },
  clothingCard: {
    borderRadius: 8,
    padding: 12,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    borderWidth: 1,
  },
  clothingImage: {
    width: '100%',
    height: 180,
    borderRadius: 4,
    marginBottom: 12,
  },
  clothingName: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 8,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  tag: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 12,
    marginRight: 4,
    marginBottom: 4,
  },
  tagText: {
    fontSize: 12,
  },
  generateButton: {
    marginTop: 16,
  },
  suggestionSection: {
    marginBottom: 24,
  },
  suggestionText: {
    fontSize: 14,
    lineHeight: 22,
    padding: 16,
    borderRadius: 8,
  },
});

export default WearThisTodayScreen;
